/*
 * Convert to Infographic — native shape rebuild (last-resort insertion path).
 *
 * insertSlidesFromBase64 does strict OOXML schema validation, but the AI
 * composer's shape-tree XML is untrusted output that can come back with
 * schema violations sanitizeComposerXml (infographicInsert.ts) doesn't yet
 * anticipate — e.g. a <p:cxnSp> with <a:ln> before <a:noFill/>, a violation
 * the backend's own well-formedness-only validation never catches, so it
 * reaches the client as `valid: true`. Rather than chasing every possible
 * malformation one at a time, this rebuilds each shape from scratch via
 * PptxGenJS's native addShape/addText API, which always emits schema-valid
 * OOXML — reading only position, size, shape type, fill colour, and text
 * runs out of the AI's XML and discarding everything else. Used as the final
 * fallback tier in useConvertInfographic.ts; the primary path keeps more of
 * the composer's original visual fidelity when it works.
 */
import PptxGenJS from "pptxgenjs";
import { strFromU8, unzipSync } from "fflate";
import { NS_A, NS_P, NS_R } from "../../../const";
import type { SlideDimensions, ThemeColors } from "../../create-slide/service/slideGenerationApi";
import { NO_INFOGRAPHIC_XML_MESSAGE, u8ToBase64 } from "./infographicInsert";

const EMU_PER_INCH = 914400;
const CUSTOM_LAYOUT_NAME = "INFOGRAPHIC_NATIVE_SLIDE";

/** Shape presets proven to survive insertSlidesFromBase64 — everything else
 *  downgrades to "rect" rather than risking an unsupported preset. */
function safeShapeType(prst: string | null): "roundRect" | "ellipse" | "rect" {
  if (prst === "roundRect") return "roundRect";
  if (prst === "ellipse") return "ellipse";
  return "rect";
}

const HEX_RE = /^[0-9A-Fa-f]{6}$/;

function safeColor(hex: string | null | undefined, fallback: string): string {
  if (!hex) return fallback;
  const clean = hex.trim().replace(/^#/, "");
  if (HEX_RE.test(clean)) return clean;
  if (/^[0-9A-Fa-f]{3}$/.test(clean)) {
    return clean[0]! + clean[0] + clean[1] + clean[1] + clean[2] + clean[2];
  }
  return fallback;
}

/** Office theme default hues — used only when a composer response emits a
 *  <a:schemeClr> despite being told the deck's real theme colours, and that
 *  scheme name isn't one of the theme colours we were actually given. */
const SCHEME_COLOR_DEFAULTS: Record<string, string> = {
  dk1: "000000",
  dk2: "44546A",
  lt1: "FFFFFF",
  lt2: "E7E6E6",
  accent1: "4472C4",
  accent2: "ED7D31",
  accent3: "A5A5A5",
  accent4: "FFC000",
  accent5: "5B9BD5",
  accent6: "70AD47",
  hlink: "0563C1",
  folHlink: "954F72",
};

function resolveSchemeColor(name: string, themeColors: ThemeColors | null): string {
  const fromTheme = themeColors ? (themeColors as Record<string, string | undefined>)[name] : undefined;
  return safeColor(fromTheme, SCHEME_COLOR_DEFAULTS[name] ?? "CCCCCC");
}

function findChild(parent: Element, ns: string, localName: string): Element | null {
  for (const child of Array.from(parent.children)) {
    if (child.namespaceURI === ns && child.localName === localName) return child;
  }
  return null;
}

function localChildren(parent: Element, localName: string): Element[] {
  return Array.from(parent.children).filter((c) => c.localName === localName);
}

/** Extracts a shape's fill as a plain hex string, or null for noFill/absent —
 *  the only shapes this addShape can render being a flat solidFill. */
function extractFillColor(spPr: Element, themeColors: ThemeColors | null): string | null {
  for (const child of Array.from(spPr.children)) {
    if (child.localName === "solidFill") {
      const srgb = findChild(child, NS_A, "srgbClr");
      if (srgb) return safeColor(srgb.getAttribute("val"), "CCCCCC");
      const scheme = findChild(child, NS_A, "schemeClr");
      if (scheme) return resolveSchemeColor(scheme.getAttribute("val") ?? "", themeColors);
      return "CCCCCC";
    }
    if (child.localName === "noFill") return null;
  }
  return null;
}

/** Connector-only: a <p:cxnSp>'s <a:ln> colour, used to render it as a thin filled rect. */
function extractLineColor(spPr: Element, themeColors: ThemeColors | null): string | null {
  const ln = localChildren(spPr, "ln")[0];
  if (!ln) return null;
  if (localChildren(ln, "noFill").length > 0) return null;
  const solidFill = findChild(ln, NS_A, "solidFill");
  if (!solidFill) return null;
  const srgb = findChild(solidFill, NS_A, "srgbClr");
  if (srgb) return safeColor(srgb.getAttribute("val"), "CCCCCC");
  const scheme = findChild(solidFill, NS_A, "schemeClr");
  if (scheme) return resolveSchemeColor(scheme.getAttribute("val") ?? "", themeColors);
  return null;
}

interface TextRunOptions {
  fontSize?: number;
  bold?: boolean;
  italic?: boolean;
  color?: string;
  fontFace?: string;
  align?: "left" | "center" | "right" | "justify";
  bullet?: boolean;
  breakLine?: boolean;
  valign?: "top" | "middle" | "bottom";
}

interface TextRun {
  text: string;
  options: TextRunOptions;
}

const ALIGN_MAP: Record<string, TextRunOptions["align"]> = { ctr: "center", r: "right", just: "justify", l: "left" };

function getParagraphAlign(pPr: Element | null): TextRunOptions["align"] | undefined {
  const algn = pPr?.getAttribute("algn") ?? null;
  return algn ? ALIGN_MAP[algn] : undefined;
}

function paragraphHasBullet(pPr: Element | null): boolean {
  return !!pPr && (localChildren(pPr, "buChar").length > 0 || localChildren(pPr, "buAutoNum").length > 0);
}

/** Only inserts a break once something has actually been emitted — avoids a
 *  leading empty breakLine when a paragraph or txBody starts with <a:br/>. */
function pushBreakIfNeeded(runs: TextRun[]): void {
  if (runs.length > 0) runs.push({ text: "", options: { breakLine: true } });
}

function buildRunTextOptions(el: Element, themeColors: ThemeColors | null): TextRunOptions {
  const rPr = localChildren(el, "rPr")[0] ?? null;
  const options: TextRunOptions = {};

  const sz = rPr?.getAttribute("sz");
  if (sz) {
    const fontSize = Number(sz) / 100;
    if (Number.isFinite(fontSize) && fontSize > 0 && fontSize < 200) options.fontSize = fontSize;
  }
  if (rPr?.getAttribute("b") === "1") options.bold = true;
  if (rPr?.getAttribute("i") === "1") options.italic = true;
  if (!rPr) return options;

  const srgb = findChild(rPr, NS_A, "srgbClr");
  if (srgb) {
    options.color = safeColor(srgb.getAttribute("val"), "333333");
  } else {
    const scheme = findChild(rPr, NS_A, "schemeClr");
    if (scheme) options.color = resolveSchemeColor(scheme.getAttribute("val") ?? "", themeColors);
  }
  const latin = findChild(rPr, NS_A, "latin");
  const typeface = latin?.getAttribute("typeface");
  if (typeface) options.fontFace = typeface;

  return options;
}

interface ParagraphRunState {
  isFirst: boolean;
  lastFontSize: number | null;
}

function appendRunFromTextElement(
  runs: TextRun[],
  el: Element,
  pi: number,
  align: TextRunOptions["align"] | undefined,
  hasBullet: boolean,
  state: ParagraphRunState,
  themeColors: ThemeColors | null,
): void {
  const tEl = localChildren(el, "t")[0] ?? null;
  const rawText = tEl?.textContent ?? "";
  const options = buildRunTextOptions(el, themeColors);

  const shouldForceBreak =
    !state.isFirst && options.fontSize != null && state.lastFontSize != null && options.fontSize !== state.lastFontSize;

  if (state.isFirst) {
    if (align) options.align = align;
    if (hasBullet) options.bullet = true;
    if (pi > 0) options.breakLine = true;
  } else if (shouldForceBreak) {
    options.breakLine = true;
  }

  runs.push({ text: rawText, options });
  state.isFirst = false;
  if (options.fontSize) state.lastFontSize = options.fontSize;
}

function appendParagraphRuns(
  runs: TextRun[],
  p: Element,
  pi: number,
  paragraphCount: number,
  themeColors: ThemeColors | null,
): void {
  const pPr = localChildren(p, "pPr")[0] ?? null;
  const align = getParagraphAlign(pPr);
  const hasBullet = paragraphHasBullet(pPr);

  const contentEls = Array.from(p.children).filter(
    (c) => c.localName === "r" || c.localName === "br" || c.localName === "fld",
  );

  if (contentEls.length === 0) {
    if (pi < paragraphCount - 1) pushBreakIfNeeded(runs);
    return;
  }

  const state: ParagraphRunState = { isFirst: true, lastFontSize: null };

  for (const el of contentEls) {
    if (el.localName === "br") {
      pushBreakIfNeeded(runs);
      state.lastFontSize = null;
      continue;
    }
    appendRunFromTextElement(runs, el, pi, align, hasBullet, state, themeColors);
  }
}

/** Flattens a <p:txBody> into PptxGenJS text runs: paragraph breaks, <a:br/>
 *  breaks, and a forced break whenever adjacent runs in the same paragraph
 *  carry different explicit font sizes (the composer often puts a large
 *  stat and its small label in one <a:p> with no separator). */
function extractTextRuns(txBody: Element, themeColors: ThemeColors | null): TextRun[] {
  const runs: TextRun[] = [];
  const paragraphs = localChildren(txBody, "p");
  paragraphs.forEach((p, pi) => appendParagraphRuns(runs, p, pi, paragraphs.length, themeColors));
  return runs;
}

/** Position/size shared by shapes and connectors: EMU -> inches, clamped to
 *  the slide bounds so a composer shape that overshoots never gets rejected
 *  for extending past the deck's own dimensions. */
function readClampedBox(
  spPr: Element,
  slideW: number,
  slideH: number,
  margin: number,
): { x: number; y: number; w: number; h: number } | null {
  const xfrm = findChild(spPr, NS_A, "xfrm");
  if (!xfrm) return null;
  const off = findChild(xfrm, NS_A, "off");
  const ext = findChild(xfrm, NS_A, "ext");
  if (!off || !ext) return null;

  let x = Number(off.getAttribute("x") ?? 0) / EMU_PER_INCH;
  let y = Number(off.getAttribute("y") ?? 0) / EMU_PER_INCH;
  let w = Number(ext.getAttribute("cx") ?? 0) / EMU_PER_INCH;
  let h = Number(ext.getAttribute("cy") ?? 0) / EMU_PER_INCH;
  if (![x, y, w, h].every(Number.isFinite)) return null;

  x = Math.max(0, Math.min(x, slideW - margin));
  y = Math.max(0, Math.min(y, slideH - margin));
  if (x + w > slideW) w = slideW - x;
  if (y + h > slideH) h = slideH - y;

  return { x, y, w, h };
}

/** Slide API surface this module actually calls — narrower than PptxGenJS's
 *  full Slide type so the module stays easy to unit test with a stub. */
interface NativeSlide {
  addShape(shapeType: string, opts: Record<string, unknown>): unknown;
  addText(runs: TextRun[], opts: Record<string, unknown>): unknown;
}

/** Most-used font face across a shape's runs — PptxGenJS needs one container
 *  fontFace even though individual runs may each carry their own override. */
function pickContainerFont(textRuns: TextRun[]): string {
  const fontCounts = new Map<string, number>();
  for (const run of textRuns) {
    if (!run.options.fontFace) continue;
    fontCounts.set(run.options.fontFace, (fontCounts.get(run.options.fontFace) ?? 0) + 1);
  }
  return Array.from(fontCounts.entries()).sort((a, b) => b[1] - a[1])[0]?.[0] ?? "Calibri";
}

function resolveValign(bodyPr: Element | null): TextRunOptions["valign"] {
  const anchor = bodyPr?.getAttribute("anchor");
  if (anchor === "ctr") return "middle";
  if (anchor === "b") return "bottom";
  return "top";
}

function addNativeShapeText(
  slide: NativeSlide,
  txBody: Element,
  textRuns: TextRun[],
  box: { x: number; y: number },
  w: number,
  h: number,
): void {
  const containerFont = pickContainerFont(textRuns);
  const valign = resolveValign(findChild(txBody, NS_A, "bodyPr"));
  slide.addText(textRuns, { x: box.x, y: box.y, w, h, fontFace: containerFont, valign });
}

function addNativeShape(slide: NativeSlide, sp: Element, slideW: number, slideH: number, themeColors: ThemeColors | null): boolean {
  const spPr = findChild(sp, NS_P, "spPr");
  if (!spPr) return false;

  const box = readClampedBox(spPr, slideW, slideH, 0.1);
  if (!box || box.w < 0.01 || box.h < 0.01) return false;
  const w = Math.max(box.w, 0.1);
  const h = Math.max(box.h, 0.1);

  const geom = findChild(spPr, NS_A, "prstGeom");
  const prst = safeShapeType(geom?.getAttribute("prst") ?? null);
  const fillColor = extractFillColor(spPr, themeColors);

  const shapeOpts: Record<string, unknown> = { x: box.x, y: box.y, w, h, fill: { color: fillColor ?? undefined } };
  if (prst === "roundRect") shapeOpts.rectRadius = 0.1;
  if (fillColor) slide.addShape(prst, shapeOpts);

  const txBody = findChild(sp, NS_P, "txBody");
  const textRuns = txBody ? extractTextRuns(txBody, themeColors) : [];
  if (txBody && textRuns.length > 0) addNativeShapeText(slide, txBody, textRuns, box, w, h);

  return true;
}

function addNativeConnector(slide: NativeSlide, cxnSp: Element, slideW: number, slideH: number, themeColors: ThemeColors | null): boolean {
  const spPr = findChild(cxnSp, NS_P, "spPr");
  if (!spPr) return false;

  const box = readClampedBox(spPr, slideW, slideH, 0.05);
  if (!box) return false;
  const cx = Math.max(box.w, 0);
  const cy = Math.max(box.h, 0);

  const color = extractLineColor(spPr, themeColors) ?? "CCCCCC";
  const LINE_THICKNESS_IN = 0.02;

  if (cy < 0.05) {
    slide.addShape("rect", { x: box.x, y: box.y, w: Math.max(cx, 0.1), h: LINE_THICKNESS_IN, fill: { color } });
  } else {
    slide.addShape("rect", { x: box.x, y: box.y, w: LINE_THICKNESS_IN, h: Math.max(cy, 0.1), fill: { color } });
  }
  return true;
}

export interface NativeInfographicBuild {
  readonly base64: string;
  readonly slideXml: string;
  readonly shapeCount: number;
}

/**
 * Rebuilds the composer's shape tree via PptxGenJS's native shape API and
 * returns a ready-to-insert base64 .pptx (the whole PptxGenJS output — no
 * skeleton-swap needed, since nothing hand-authored gets injected into it)
 * plus the generated slide's own XML, for callers (region-merge) that need
 * to splice individual shape elements rather than insert a whole new slide.
 * Throws when the composer XML is malformed or yields no usable shapes.
 */
export async function buildNativeInfographicSlide(
  spTreeXml: string,
  dims: SlideDimensions,
  themeColors: ThemeColors | null = null,
): Promise<NativeInfographicBuild> {
  const doc = new DOMParser().parseFromString(
    `<root xmlns:a="${NS_A}" xmlns:r="${NS_R}" xmlns:p="${NS_P}">${spTreeXml || ""}</root>`,
    "application/xml",
  );
  if (doc.getElementsByTagName("parsererror").length > 0) throw new Error(NO_INFOGRAPHIC_XML_MESSAGE);

  const slideW = dims.cx / EMU_PER_INCH;
  const slideH = dims.cy / EMU_PER_INCH;

  const pptx = new PptxGenJS();
  pptx.defineLayout({ name: CUSTOM_LAYOUT_NAME, width: slideW, height: slideH });
  pptx.layout = CUSTOM_LAYOUT_NAME;
  const slide = pptx.addSlide() as unknown as NativeSlide;

  let shapeCount = 0;
  for (const sp of Array.from(doc.getElementsByTagNameNS(NS_P, "sp"))) {
    if (addNativeShape(slide, sp, slideW, slideH, themeColors)) shapeCount++;
  }
  for (const cxn of Array.from(doc.getElementsByTagNameNS(NS_P, "cxnSp"))) {
    if (addNativeConnector(slide, cxn, slideW, slideH, themeColors)) shapeCount++;
  }

  if (shapeCount === 0) throw new Error(NO_INFOGRAPHIC_XML_MESSAGE);

  const arrayBuf = (await pptx.write({ outputType: "arraybuffer" })) as ArrayBuffer;
  const bytes = new Uint8Array(arrayBuf);
  const entries = unzipSync(bytes);
  const slideXmlBytes = entries["ppt/slides/slide1.xml"];
  const slideXml = slideXmlBytes ? strFromU8(slideXmlBytes) : "";

  return { base64: u8ToBase64(bytes), slideXml, shapeCount };
}
